import { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/search-input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Download,
  Trash2,
  FileText,
  Upload,
  Eye,
  Search,
  Copy,
  Check,
  ChevronDown,
  ChevronRight,
  BookOpen,
  Users,
  Briefcase,
  Building2,
  DollarSign,
  Clock,
  Receipt,
  Table,
  CreditCard,
  FileX,
  Tag,
  Info,
  Loader2,
  AlertCircle,
  Save,
} from 'lucide-react';
import api from '@/lib/api';
import { Firm } from '@/types/settings';

// --- Types for backend response ---
type DocumentType = 'invoice' | 'credit_note' | 'down_payment';

interface TemplateVariable {
  tag: string;
  description: string;
  type: 'inline' | 'loop';
  example: string;
}

interface VariableCategory {
  key: string;
  label: string;
  icon: string;
  variables: TemplateVariable[];
}

interface LoopField {
  field: string;
  description: string;
}

interface TemplateVariablesResponse {
  document_type: string;
  categories: VariableCategory[];
  loop_fields: Record<string, LoopField[]>;
  description: string;
}

// --- Icon map ---
const ICON_MAP: Record<string, React.ReactNode> = {
  FileText: <FileText className="w-4 h-4" />,
  Users: <Users className="w-4 h-4" />,
  Briefcase: <Briefcase className="w-4 h-4" />,
  Building2: <Building2 className="w-4 h-4" />,
  DollarSign: <DollarSign className="w-4 h-4" />,
  Clock: <Clock className="w-4 h-4" />,
  Receipt: <Receipt className="w-4 h-4" />,
  Table: <Table className="w-4 h-4" />,
  CreditCard: <CreditCard className="w-4 h-4" />,
  FileX: <FileX className="w-4 h-4" />,
  Tag: <Tag className="w-4 h-4" />,
};

// --- Props ---
interface DocumentTemplateSettingsProps {
  firm: Firm;
  canModify: boolean;
  onUpdate: () => void;
}

// --- Copy button ---
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={handleCopy}
          >
            {copied ? (
              <Check className="h-3 w-3 text-green-600" />
            ) : (
              <Copy className="h-3 w-3 text-muted-foreground" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="left">
          <p>{copied ? 'Copied!' : 'Copy to clipboard'}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// --- Variable Reference Dialog ---
function VariableReferenceDialog({ firmId, documentType }: { firmId: number; documentType: DocumentType }) {
  const [data, setData] = useState<TemplateVariablesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState(false);

  const fetchVariables = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/api/firms/${firmId}/template_variables/`, {
        params: { type: documentType },
      });
      setData(response.data);
      // Expand all categories by default
      const cats = response.data.categories ?? [];
      setExpandedCategories(new Set(cats.map((c: VariableCategory) => c.key)));
    } catch {
      setError('Failed to load template variables');
    } finally {
      setLoading(false);
    }
  }, [firmId, documentType]);

  useEffect(() => {
    if (open) {
      fetchVariables();
    } else {
      setSearch('');
    }
  }, [open, fetchVariables]);

  const toggleCategory = (key: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedCategories(new Set((data?.categories ?? []).map((c) => c.key)));
  };

  const collapseAll = () => {
    setExpandedCategories(new Set());
  };

  // Filter variables by search
  const searchLower = search.toLowerCase();
  const filteredCategories = (data?.categories ?? [])
    .map((cat) => ({
      ...cat,
      variables: cat.variables.filter(
        (v) =>
          v.tag.toLowerCase().includes(searchLower) ||
          v.description.toLowerCase().includes(searchLower)
      ),
    }))
    .filter((cat) => cat.variables.length > 0);

  const totalVarCount = filteredCategories.reduce((sum, cat) => sum + cat.variables.length, 0);

  const docTypeLabels: Record<DocumentType, string> = {
    invoice: 'Invoice',
    credit_note: 'Credit Note',
    down_payment: 'Down Payment',
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <BookOpen className="w-3.5 h-3.5" />
          Variable Reference
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl !h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
        {/* Fixed header */}
        <div className="shrink-0 px-6 pt-6 pb-4 border-b space-y-3">
          <DialogHeader>
            <DialogTitle className="text-lg">Template Variables — {docTypeLabels[documentType]}</DialogTitle>
            <DialogDescription>
              Copy these tags and paste them into your Word template. They will be replaced with real data when generating documents.
            </DialogDescription>
          </DialogHeader>

          {/* Search + controls */}
          <div className="flex items-center gap-2">
            <SearchInput
              placeholder="Search variables..."
              value={search}
              onChange={setSearch}
              className="flex-1"
            />
            <div className="flex items-center gap-1 shrink-0">
              <Button variant="ghost" size="sm" onClick={expandAll} className="text-xs h-8 px-2">
                Expand all
              </Button>
              <Button variant="ghost" size="sm" onClick={collapseAll} className="text-xs h-8 px-2">
                Collapse all
              </Button>
            </div>
          </div>

          {/* Results count */}
          {data && !loading && (
            <p className="text-xs text-muted-foreground">
              {search
                ? `${totalVarCount} variable${totalVarCount !== 1 ? 's' : ''} matching "${search}"`
                : `${totalVarCount} variables across ${filteredCategories.length} categories`}
            </p>
          )}
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
          {error && (
            <Alert variant="destructive" className="my-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {!loading && !error && filteredCategories.length === 0 && data && (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Search className="h-8 w-8 mb-3 opacity-40" />
              <p className="text-sm font-medium">No variables found</p>
              <p className="text-xs mt-1">Try a different search term</p>
            </div>
          )}
          {filteredCategories.length > 0 && (
            <div className="space-y-1">
              {filteredCategories.map((cat) => (
                <Collapsible
                  key={cat.key}
                  open={expandedCategories.has(cat.key)}
                  onOpenChange={() => toggleCategory(cat.key)}
                >
                  <CollapsibleTrigger className="flex items-center gap-2 w-full py-2.5 px-3 rounded-lg hover:bg-muted/50 transition-colors text-left group">
                    {expandedCategories.has(cat.key) ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0 transition-transform" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 transition-transform" />
                    )}
                    <span className="shrink-0">{ICON_MAP[cat.icon] || <Tag className="w-4 h-4" />}</span>
                    <span className="font-medium text-sm">{cat.label}</span>
                    <Badge variant="secondary" className="ml-auto text-[10px] tabular-nums">
                      {cat.variables.length}
                    </Badge>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="ml-5 mb-2 border-l-2 border-muted">
                      <table className="w-full">
                        <tbody>
                          {cat.variables.map((v) => (
                            <tr key={v.tag} className="group hover:bg-muted/30">
                              <td className="py-1.5 pl-4 pr-2 align-top w-[260px]">
                                <code className="text-xs bg-muted px-2 py-1 rounded font-mono whitespace-nowrap select-all inline-block">
                                  {v.tag}
                                </code>
                              </td>
                              <td className="py-1.5 px-2 align-middle">
                                <span className="text-sm text-muted-foreground leading-snug">
                                  {v.description}
                                </span>
                              </td>
                              <td className="py-1.5 pl-2 pr-3 align-middle w-8">
                                <CopyButton text={v.tag} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              ))}

              {/* Loop fields reference */}
              {data?.loop_fields && Object.keys(data.loop_fields).length > 0 && (
                <div className="mt-6 border rounded-lg p-5 bg-muted/20">
                  <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <Table className="w-4 h-4" />
                    Fields Available Inside Loops
                  </h4>
                  <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
                    When iterating over timesheets or costs with{' '}
                    <code className="bg-muted px-1.5 py-0.5 rounded text-[11px]">{'{% for ts in timesheets %}'}</code>{' '}
                    or <code className="bg-muted px-1.5 py-0.5 rounded text-[11px]">{'{% for cost in costs %}'}</code>,
                    these fields are available on each item:
                  </p>
                  {Object.entries(data.loop_fields).map(([loopName, fields]) => (
                    <div key={loopName} className="mb-4 last:mb-0">
                      <p className="text-xs font-semibold mb-2 capitalize flex items-center gap-2">
                        {loopName === 'timesheets' ? <Clock className="w-3.5 h-3.5" /> : <Receipt className="w-3.5 h-3.5" />}
                        {loopName}
                      </p>
                      <table className="w-full">
                        <tbody>
                          {fields.map((f) => (
                            <tr key={f.field} className="group hover:bg-muted/30">
                              <td className="py-1.5 pl-2 pr-2 align-top w-[260px]">
                                <code className="text-xs bg-background px-2 py-1 rounded font-mono whitespace-nowrap select-all inline-block">
                                  {'{{ '}{f.field}{' }}'}
                                </code>
                              </td>
                              <td className="py-1.5 px-2 align-middle">
                                <span className="text-sm text-muted-foreground leading-snug">
                                  {f.description}
                                </span>
                              </td>
                              <td className="py-1.5 pl-2 pr-1 align-middle w-8">
                                <CopyButton text={`{{ ${f.field} }}`} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --- Main Component ---
export function DocumentTemplateSettings({ firm, canModify, onUpdate }: DocumentTemplateSettingsProps) {
  const [uploading, setUploading] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [generating, setGenerating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DocumentType>('invoice');

  // Default invoice description state
  const [defaultDescription, setDefaultDescription] = useState(firm.default_invoice_description || '');
  const [savingDescription, setSavingDescription] = useState(false);
  const descriptionChanged = defaultDescription !== (firm.default_invoice_description || '');

  // Sync state when firm prop changes
  useEffect(() => {
    setDefaultDescription(firm.default_invoice_description || '');
  }, [firm.default_invoice_description]);

  const handleSaveDescription = async () => {
    if (!canModify) return;
    try {
      setSavingDescription(true);
      setError(null);
      await api.patch(`/api/firms/${firm.id}/`, {
        default_invoice_description: defaultDescription,
      });
      setSuccess('Default invoice description saved');
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save default description');
    } finally {
      setSavingDescription(false);
    }
  };

  const templateTypes: {
    key: DocumentType;
    name: string;
    uploadEndpoint: string;
    deleteEndpoint: string;
    description: string;
  }[] = [
    {
      key: 'invoice',
      name: 'Invoice Template',
      uploadEndpoint: 'upload_invoice_template',
      deleteEndpoint: 'delete_invoice_template',
      description: 'Used when generating invoice documents. Includes timesheet entries, costs, and financial calculations.',
    },
    {
      key: 'credit_note',
      name: 'Credit Note Template',
      uploadEndpoint: 'upload_credit_note_template',
      deleteEndpoint: 'delete_credit_note_template',
      description: 'Used when generating credit note documents. Includes credit amount and original invoice reference.',
    },
    {
      key: 'down_payment',
      name: 'Down Payment Template',
      uploadEndpoint: 'upload_down_payment_template',
      deleteEndpoint: 'delete_down_payment_template',
      description: 'Used when generating down payment invoices. Includes payment amount and tracking details.',
    },
  ];

  const handleFileUpload = async (templateType: DocumentType, file: File) => {
    if (!canModify) return;
    const template = templateTypes.find((t) => t.key === templateType);
    if (!template) return;

    try {
      setUploading(templateType);
      setError(null);
      setSuccess(null);
      const formData = new FormData();
      formData.append('template', file);
      await api.post(`/api/firms/${firm.id}/${template.uploadEndpoint}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(`${template.name} uploaded successfully`);
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.error || `Failed to upload ${template.name}`);
    } finally {
      setUploading(null);
    }
  };

  const handleFileDelete = async (templateType: DocumentType) => {
    if (!canModify) return;
    const template = templateTypes.find((t) => t.key === templateType);
    if (!template) return;
    if (!confirm(`Are you sure you want to delete the ${template.name}?`)) return;

    try {
      setDeleting(templateType);
      setError(null);
      setSuccess(null);
      await api.delete(`/api/firms/${firm.id}/${template.deleteEndpoint}/`);
      setSuccess(`${template.name} deleted successfully`);
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.error || `Failed to delete ${template.name}`);
    } finally {
      setDeleting(null);
    }
  };

  const handleDownloadTemplate = async (templateType: DocumentType) => {
    try {
      const response = await api.get(`/api/firms/${firm.id}/download_template/`, {
        params: { template_type: templateType },
        responseType: 'blob',
      });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const templateStatus = getTemplateStatus(templateType);
      a.download = templateStatus.name ? templateStatus.name.split('/').pop() || 'template.docx' : 'template.docx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download template. Please try again.');
    }
  };

  const generateSampleDocument = async (documentType: DocumentType) => {
    try {
      setGenerating(documentType);
      setError(null);
      setSuccess(null);
      const response = await api.post(
        `/api/firms/${firm.id}/generate_sample_document/`,
        { type: documentType },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${documentType}_sample.docx`;
      a.click();
      window.URL.revokeObjectURL(url);
      setSuccess('Sample document generated and downloaded');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate sample document');
    } finally {
      setGenerating(null);
    }
  };

  const getTemplateStatus = (templateType: string) => {
    switch (templateType) {
      case 'invoice':
        return firm.template_status.invoice_template;
      case 'credit_note':
        return firm.template_status.credit_note_template;
      case 'down_payment':
        return firm.template_status.down_payment_template;
      default:
        return { uploaded: false, name: undefined, url: undefined };
    }
  };

  // Auto-clear messages after 5s
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Document Templates
          </CardTitle>
          <CardDescription>
            Upload Word (.docx) templates with placeholder variables. When generating documents, the
            variables are automatically replaced with real invoice data. Edit templates in Microsoft Word,
            Google Docs, or any word processor that supports .docx files.
          </CardDescription>
        </CardHeader>
      </Card>

      {!canModify && (
        <Alert>
          <AlertDescription>You don't have permission to modify document templates.</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-200 bg-green-50">
          <Check className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      {/* Default Invoice Description */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Default Invoice Description
          </CardTitle>
          <CardDescription>
            Set a default description that appears on all new invoices. You can use template
            variables like <code className="bg-muted px-1.5 py-0.5 rounded text-xs">{'{{ begin_date }}'}</code>,{' '}
            <code className="bg-muted px-1.5 py-0.5 rounded text-xs">{'{{ end_date }}'}</code>,{' '}
            <code className="bg-muted px-1.5 py-0.5 rounded text-xs">{'{{ matter_name }}'}</code>,{' '}
            <code className="bg-muted px-1.5 py-0.5 rounded text-xs">{'{{ client_name }}'}</code>{' '}
            — they will be replaced with real data when the invoice is generated.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label htmlFor="default-description">Description</Label>
            <Textarea
              id="default-description"
              value={defaultDescription}
              onChange={(e) => setDefaultDescription(e.target.value)}
              placeholder="e.g. Services rendered from {{ begin_date }} to {{ end_date }} for {{ matter_name }}"
              rows={3}
              disabled={!canModify}
              className="mt-1.5 font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground mt-1.5">
              This description can be overridden per invoice when creating it. Leave blank for no default.
            </p>
          </div>
          {canModify && (
            <Button
              onClick={handleSaveDescription}
              disabled={savingDescription || !descriptionChanged}
              size="sm"
              className="gap-1.5"
            >
              {savingDescription ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              {savingDescription ? 'Saving...' : 'Save'}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Templates by type */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as DocumentType)}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="invoice" className="gap-1.5">
            <FileText className="h-3.5 w-3.5" />
            Invoice
          </TabsTrigger>
          <TabsTrigger value="credit_note" className="gap-1.5">
            <FileX className="h-3.5 w-3.5" />
            Credit Note
          </TabsTrigger>
          <TabsTrigger value="down_payment" className="gap-1.5">
            <CreditCard className="h-3.5 w-3.5" />
            Down Payment
          </TabsTrigger>
        </TabsList>

        {templateTypes.map((template) => {
          const templateStatus = getTemplateStatus(template.key);
          const isUploading = uploading === template.key;
          const isDeleting = deleting === template.key;
          const isGenerating = generating === template.key;

          return (
            <TabsContent key={template.key} value={template.key} className="space-y-4">
              {/* Template status card */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{template.name}</h3>
                        <Badge variant={templateStatus.uploaded ? 'default' : 'secondary'}>
                          {templateStatus.uploaded ? 'Active' : 'Not Uploaded'}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{template.description}</p>
                      {templateStatus.uploaded && templateStatus.name && (
                        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          Current file: <span className="font-mono">{templateStatus.name.split('/').pop()}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Actions row */}
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Variable reference */}
                    <VariableReferenceDialog firmId={firm.id} documentType={template.key} />

                    {templateStatus.uploaded && (
                      <>
                        {/* Preview with sample data */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => generateSampleDocument(template.key)}
                          disabled={isGenerating}
                          className="gap-1.5"
                        >
                          {isGenerating ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Eye className="w-3.5 h-3.5" />
                          )}
                          {isGenerating ? 'Generating...' : 'Generate Preview'}
                        </Button>

                        {/* Download current template */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadTemplate(template.key)}
                          className="gap-1.5"
                        >
                          <Download className="w-3.5 h-3.5" />
                          Download Template
                        </Button>

                        {/* Delete */}
                        {canModify && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleFileDelete(template.key)}
                            disabled={isDeleting}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-1.5"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            {isDeleting ? 'Deleting...' : 'Delete'}
                          </Button>
                        )}
                      </>
                    )}
                  </div>

                  {/* Upload area */}
                  {canModify && (
                    <div className="mt-6 border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                      <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm font-medium mb-1">
                        {templateStatus.uploaded ? 'Replace template' : 'Upload template'}
                      </p>
                      <p className="text-xs text-muted-foreground mb-3">
                        Drag and drop a .docx file or click to browse
                      </p>
                      <Label htmlFor={`upload-${template.key}`}>
                        <Button variant="outline" size="sm" asChild disabled={isUploading}>
                          <span>
                            {isUploading ? (
                              <>
                                <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                                Uploading...
                              </>
                            ) : (
                              'Choose File'
                            )}
                          </span>
                        </Button>
                      </Label>
                      <Input
                        id={`upload-${template.key}`}
                        type="file"
                        accept=".docx"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileUpload(template.key, file);
                          e.target.value = '';
                        }}
                        disabled={isUploading}
                        className="hidden"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* How it works guide */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Info className="h-4 w-4" />
            How Template Variables Work
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">1. Simple Variables</h4>
              <p className="text-xs text-muted-foreground">
                Place variable tags anywhere in your Word document. They'll be replaced with real data:
              </p>
              <div className="bg-muted rounded-md p-3 text-xs font-mono space-y-1">
                <p>Invoice: <span className="text-blue-600">{'{{ invoice_number }}'}</span></p>
                <p>Client: <span className="text-blue-600">{'{{ client_name }}'}</span></p>
                <p>Total: <span className="text-blue-600">{'{{ total }}'}</span></p>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-semibold">2. Conditional Content</h4>
              <p className="text-xs text-muted-foreground">
                Show content only when data exists. Great for optional sections:
              </p>
              <div className="bg-muted rounded-md p-3 text-xs font-mono space-y-1">
                <p><span className="text-purple-600">{'{% if description %}'}</span></p>
                <p>&nbsp;&nbsp;<span className="text-blue-600">{'{{ description }}'}</span></p>
                <p><span className="text-purple-600">{'{% endif %}'}</span></p>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-semibold">3. Table Rows (Loops)</h4>
              <p className="text-xs text-muted-foreground">
                Create a table in Word, then add loop tags to repeat rows for each timesheet or cost entry:
              </p>
              <div className="bg-muted rounded-md p-3 text-xs font-mono space-y-1">
                <p><span className="text-purple-600">{'{% for ts in timesheets %}'}</span></p>
                <p>&nbsp;&nbsp;<span className="text-blue-600">{'{{ ts.date }}'}</span> | <span className="text-blue-600">{'{{ ts.description }}'}</span> | <span className="text-blue-600">{'{{ ts.hours }}'}</span></p>
                <p><span className="text-purple-600">{'{% endfor %}'}</span></p>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-semibold">4. Workflow</h4>
              <p className="text-xs text-muted-foreground">The recommended process:</p>
              <ol className="text-xs text-muted-foreground list-decimal ml-4 space-y-1">
                <li>Click <strong>Variable Reference</strong> to see all available tags</li>
                <li>Design your template in Word with the tags</li>
                <li>Upload the .docx file here</li>
                <li>Click <strong>Generate Preview</strong> to test with sample data</li>
                <li>Adjust and re-upload until it looks perfect</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
